import {
  DDOS_LOGIN_REQUIRED,
  DDOS_STATUS_OPEN,
  type DdosRefusal,
  type DdosStatus
} from '@shared/all/domains/ddosMode';

import { type WrappedTRPCClient } from './trpcClientTypes';

// What the server last said about DDoS mode for this caller
// (.specs/ddos-mode.md). One module-level store, because it is written from
// outside React twice over: by the boot read in `useAuth.initializeUser`, and
// by each client's tRPC link when a call comes back refused, which is how a
// flag flipped mid-session takes effect without a reload.
//
// Nothing here may import React. The web tRPC client imports this module and
// is itself imported by server components, where a hook import fails the
// whole render. The hook that reads this store is hooks/useDdosStatus.ts.

let status: DdosStatus = DDOS_STATUS_OPEN;
const listeners = new Set<() => void>();

const setStatus = (next: DdosStatus): void => {
  if (next.ddosMode === status.ddosMode && next.access === status.access)
    return;
  status = next;
  listeners.forEach((listener) => listener());
};

export const subscribeDdosStatus = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): DdosStatus => status;
// The server render never knows: it renders as open, and the client corrects
// itself after its first read.
export const getServerDdosStatus = (): DdosStatus => DDOS_STATUS_OPEN;

export const getDdosStatus = getSnapshot;

/** A refused call is the server stating the status without being asked. */
export const noteDdosRefusal = (refusal: DdosRefusal): void => {
  setStatus({
    ddosMode: true,
    access:
      refusal === DDOS_LOGIN_REQUIRED
        ? 'loginRequired'
        : 'humanVerificationRequired'
  });
};

/**
 * Ask the server. A failed read (offline, a deploy) says nothing about the
 * mode, so the last known status stands.
 */
export const refreshDdosStatus = async (
  client: WrappedTRPCClient
): Promise<DdosStatus> => {
  const [error, next] = await client.ddosStatus.query(undefined, {
    context: { silenceErrors: true }
  });
  if (error || !next) return status;
  setStatus(next);
  return next;
};
